import { CompanyTypes } from "@shared_packages/toolkit/types";
import { type ColumnDef } from "@tanstack/react-table";
import React, { type FC } from "react";
import { useNavigate } from "react-router-dom";
import {
  Actions,
  Button,
  ButtonVariant,
  CopyIDInput,
  DateTableCell,
  ModalHeader,
  PaginationControl,
  ReactTableComponent,
  RowSelectionControl,
  Status,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { AppRoutes } from "../../../../constants/routes";
import { BankAccountApiType, CompanyApiType } from "../../../../util/types";
import styled from "styled-components";
import { useModalManager } from "../../../../context/ModalManager";
import { useVerifyDomain } from "../../companies/hooks/useVerifyDomain";
import { useVerifyEmailDomain } from "../../companies/hooks/useVerifyEmailDomain";
import { useVerifyEmailSender } from "../../companies/hooks/useVerifyEmailSender";

type CompanyListSubscription = {
  subscriptionId?: string | number | null;
  subscriptionOption?: string | number | null;
  status?: string | number | null;
};

const Modal = styled.div`
  max-height: 90vh;
  overflow: auto;
`;

const ModalContent = styled.div`
  padding: 30px;
`;

const Cell = styled.div`
  padding: 15px 10px;
`;

// Unbroken values (e.g. a name with no spaces) otherwise set the column's
// min width and push the whole table past its card; min-width stops normal
// names from breaking letter by letter on narrow screens.
const WrapCell = styled(Cell)`
  min-width: 140px;
  overflow-wrap: anywhere;
`;

const QuickLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
`;

const QuickLink = styled.button`
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: #027aff;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
`;

const SubscriptionLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  text-transform: capitalize;
  color: #333;
`;

const SubscriptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SubscriptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CreatedByBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CreatedByEmail = styled.span`
  font-size: 12px;
  color: #666;
  word-break: break-all;
`;

const PartnerTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  margin-top: 4px;
  border-radius: 5px;
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  width: fit-content;
  background: rgba(2, 122, 255, 0.12);
  color: #027aff;
`;

const TypeCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const COMPANY_TYPE_LABELS: Partial<Record<CompanyTypes, string>> = {
  [CompanyTypes.COMPANY]: "Company",
  [CompanyTypes.CONSUMER]: "Consumer",
  [CompanyTypes.SOLE_TRADER]: "Sole trader",
};

function formatCompanyListType(type: string | undefined): string {
  if (type == null || type === "") return "-";
  const mapped = COMPANY_TYPE_LABELS[type as CompanyTypes];
  if (mapped) return mapped;
  return type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

interface CompaniesTableProps {
  data: CompanyApiType[];
  pagination: PaginationControl;
  withSelect?: boolean;
  onAddCompany?: (val: { name?: string; id?: string }) => void;
  rowSelection?: RowSelectionControl;
}

const CompaniesTable: FC<CompaniesTableProps> = ({
  data,
  pagination,
  withSelect,
  onAddCompany,
  rowSelection,
}) => {
  const { verifyDomain } = useVerifyDomain();
  const { verifyEmailDomain } = useVerifyEmailDomain();
  const { verifyEmailSender } = useVerifyEmailSender();

  const handleDomainVerification = async (row: CompanyApiType) => {
    await verifyDomain(row._id);
    removeLastModal();
  };

  const handleEmailDomainVerification = async (row: CompanyApiType) => {
    await verifyEmailDomain(row._id);
    removeLastModal();
  };

  const handleEmailSenderVerification = async (row: CompanyApiType) => {
    await verifyEmailSender(row._id);
    removeLastModal();
  };

  const navigate = useNavigate();
  const { removeLastModal, addModal } = useModalManager();
  const detailsPath = (row: CompanyApiType, segment: string) =>
    `${AppRoutes.Private.Companies.DETAILS}${row._id}/${segment}`;
  const onRowClick = (row: CompanyApiType) => {
    navigate(
      detailsPath(row, AppRoutes.Private.CompanyDetailsSection.OVERVIEW)
    );
  };
  const QUICK_LINKS: Array<{ label: string; segment: string }> = [
    {
      label: "Overview",
      segment: AppRoutes.Private.CompanyDetailsSection.OVERVIEW,
    },
    { label: "Users", segment: AppRoutes.Private.CompanyDetailsSection.USERS },
    {
      label: "Officers",
      segment: AppRoutes.Private.CompanyDetailsSection.OFFICERS,
    },
    {
      label: "Bank accounts",
      segment: AppRoutes.Private.CompanyDetailsSection.BANK_ACCOUNTS,
    },
    {
      label: "Transactions",
      segment: AppRoutes.Private.CompanyDetailsSection.TRANSACTIONS,
    },
    {
      label: "Subscriptions",
      segment: AppRoutes.Private.CompanyDetailsSection.BILLING_SUBSCRIPTIONS,
    },
  ];

  const onVerifyDomainClick = async (row: CompanyApiType) => {
    addModal(
      <Modal>
        <ModalHeader title="WARNING" handleClose={removeLastModal} />
        <ModalContent>
          <Typography variant={TypographyVariant.BODY1}>
            Are you sure you want to verify domain for this company?
          </Typography>

          <ButtonWrapper>
            <Button
              variant={ButtonVariant.CONTAINED}
              onClick={async () => {
                await handleDomainVerification(row);
              }}
              style={{ marginRight: 20 }}
            >
              Confirm
            </Button>
            <Button onClick={removeLastModal} variant={ButtonVariant.OUTLINED}>
              Cancel
            </Button>
          </ButtonWrapper>
        </ModalContent>
      </Modal>
    );
  };

  const onVerifyEmailDomainClick = async (row: CompanyApiType) => {
    addModal(
      <Modal>
        <ModalHeader title="WARNING" handleClose={removeLastModal} />
        <ModalContent>
          <Typography variant={TypographyVariant.BODY1}>
            Are you sure you want to verify email domain for this company?
          </Typography>

          <ButtonWrapper>
            <Button
              variant={ButtonVariant.CONTAINED}
              onClick={async () => {
                await handleEmailDomainVerification(row);
              }}
              style={{ marginRight: 20 }}
            >
              Confirm
            </Button>
            <Button onClick={removeLastModal} variant={ButtonVariant.OUTLINED}>
              Cancel
            </Button>
          </ButtonWrapper>
        </ModalContent>
      </Modal>
    );
  };

  const onVerifyEmailSenderClick = async (row: CompanyApiType) => {
    addModal(
      <Modal>
        <ModalHeader title="WARNING" handleClose={removeLastModal} />
        <ModalContent>
          <Typography variant={TypographyVariant.BODY1}>
            Are you sure you want to verify email sender for this company?
          </Typography>

          <ButtonWrapper>
            <Button
              variant={ButtonVariant.CONTAINED}
              onClick={async () => {
                await handleEmailSenderVerification(row);
              }}
              style={{ marginRight: 20 }}
            >
              Confirm
            </Button>
            <Button onClick={removeLastModal} variant={ButtonVariant.OUTLINED}>
              Cancel
            </Button>
          </ButtonWrapper>
        </ModalContent>
      </Modal>
    );
  };

  const handleAddCompany = (item: CompanyApiType) => {
    onAddCompany?.({ name: item.tradingName, id: item._id });
    removeLastModal();
  };

  const columns = React.useMemo<
    Array<
      ColumnDef<
        CompanyApiType & { bankAccounts: BankAccountApiType[] },
        unknown
      >
    >
  >(
    () => [
      {
        header: "Fena ID",
        accessorKey: "_id",
        enableSorting: false,
        cell: ({ getValue }) => {
          const id = String(getValue() ?? "");
          return (
            <Cell>
              <CopyIDInput uniqueTooltipId={id} hideInput text={id} />
            </Cell>
          );
        },
      },
      {
        header: "Company name",
        accessorKey: "name",
        meta: { mobileTitle: true },
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <WrapCell>{original.tradingName ?? original.name ?? "-"}</WrapCell>
        ),
      },
      {
        header: "Type",
        accessorKey: "type",
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <Cell>
            <TypeCell>
              <span>{formatCompanyListType(original.type)}</span>
              {original.canManageCompanies ? (
                <PartnerTag>Partner</PartnerTag>
              ) : null}
            </TypeCell>
          </Cell>
        ),
      },
      {
        header: "Public Email",
        accessorKey: "publicEmail",
        enableSorting: false,
        cell: ({ getValue }) => (
          <WrapCell>{String(getValue() ?? "-")}</WrapCell>
        ),
      },
      {
        header: "Created by",
        accessorKey: "createdBy",
        enableSorting: false,
        cell: ({ row: { original } }) => {
          const u = original?.createdBy as Record<string, string> | undefined;
          const name = [u?.firstName, u?.lastName]
            .filter(Boolean)
            .join(" ")
            .trim();
          const email = u?.email?.trim();
          const primary = name || email || "—";
          const showEmailSubline = Boolean(name && email);
          return (
            <Cell>
              <CreatedByBlock>
                <span>{primary}</span>
                {showEmailSubline ? (
                  <CreatedByEmail>{email}</CreatedByEmail>
                ) : null}
              </CreatedByBlock>
            </Cell>
          );
        },
      },
      {
        header: "Verification status",
        accessorKey: "verificationStatus",
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <Cell>
            <Status status={original.verificationStatus ?? "N/A"} />
          </Cell>
        ),
      },
      {
        header: "Company status",
        accessorKey: "status",
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <Cell>
            <Status status={original.status ?? "N/A"} />
          </Cell>
        ),
      },
      {
        header: "Subscription",
        accessorKey: "subscription",
        enableSorting: false,
        cell: ({ row: { original } }) => {
          const subs = (
            original as unknown as { subscription?: CompanyListSubscription[] }
          ).subscription as CompanyListSubscription[] | undefined;
          if (!subs?.length) {
            return (
              <Cell>
                <span style={{ color: "#aaa", fontSize: 13 }}>
                  No subscription
                </span>
              </Cell>
            );
          }

          return (
            <Cell>
              <SubscriptionList>
                {subs.map((sub, index) => {
                  const option = String(sub.subscriptionOption ?? "");
                  const status = String(sub.status ?? "processing");
                  const label = option
                    ? option.replace(/_/g, " ")
                    : "Unknown subscription";
                  const key = String(sub.subscriptionId ?? (option || index));

                  return (
                    <SubscriptionRow key={key}>
                      <SubscriptionLabel>{label}</SubscriptionLabel>
                      <Status status={status} />
                    </SubscriptionRow>
                  );
                })}
              </SubscriptionList>
            </Cell>
          );
        },
      },
      {
        header: "Registered",
        accessorKey: "createdAt",
        enableSorting: false,
        cell: (info) => (
          <Cell>
            <DateTableCell date={String(info.getValue() ?? "")} />
          </Cell>
        ),
      },
      {
        header: "Quick links",
        accessorKey: "quickLinks",
        id: "quickLinks",
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <Cell>
            <QuickLinks>
              {QUICK_LINKS.map((link) => (
                <QuickLink
                  key={link.segment}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(detailsPath(original, link.segment));
                  }}
                >
                  {link.label}
                </QuickLink>
              ))}
            </QuickLinks>
          </Cell>
        ),
      },
      {
        header: "Quick links",
        accessorKey: "quickLinks",
        id: "quickLinks",
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <Cell>
            <QuickLinks>
              {QUICK_LINKS.map((link) => (
                <QuickLink
                  key={link.segment}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(detailsPath(original, link.segment));
                  }}
                >
                  {link.label}
                </QuickLink>
              ))}
            </QuickLinks>
          </Cell>
        ),
      },
      {
        header: "",
        accessorKey: "actions",
        id: "actions",
        cell: ({ row: { original } }) => (
          <Cell>
            <Actions
              actions={[
                {
                  label: "View details",
                  onClick: () => {
                    onRowClick(original);
                  },
                },
                {
                  label: "Verify domain",
                  onClick: () => {
                    onVerifyDomainClick(original);
                  },
                },
                {
                  label: "Verify email domain",
                  onClick: () => {
                    onVerifyEmailDomainClick(original);
                  },
                },
                {
                  label: "Verify email sender",
                  onClick: () => {
                    onVerifyEmailSenderClick(original);
                  },
                },
              ]}
            />
          </Cell>
        ),
      },
    ],
    []
  );

  return (
    <>
      <ReactTableComponent
        columns={columns}
        data={data}
        onRowClick={withSelect ? handleAddCompany : onRowClick}
        withPagination={pagination}
        columnVisibility={{
          actions: !withSelect,
        }}
        padding="0"
        withSorting
        withRowSelection={rowSelection}
      />
    </>
  );
};

export default CompaniesTable;
